import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { ArrowRight, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import BrandMark from "../components/BrandMark";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) setError("This reset link is missing its token. Request a new one from the sign-in page.");
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/password-reset/confirm", { token, password });
      setDone(true);
      toast.success("Password updated. Please sign in.");
      setTimeout(() => navigate("/login"), 1800);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Could not reset password.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white border border-zinc-300" data-testid="reset-card">
        <div className="bg-[#1A2332] text-white px-6 py-5 flex items-center gap-3">
          <BrandMark size={36} className="rounded-md" />
          <div>
            <div className="font-display text-lg font-bold leading-none">ezItemizer</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 mt-1">
              Password reset
            </div>
          </div>
        </div>

        <div className="px-6 py-7 sm:px-8 sm:py-9">
          {done ? (
            <div className="text-center space-y-4 py-4">
              <CheckCircle size={48} weight="fill" className="text-emerald-600 mx-auto" />
              <h2 className="font-display text-xl font-bold text-zinc-900">Password updated</h2>
              <p className="text-sm text-zinc-600">Redirecting you to sign in…</p>
            </div>
          ) : (
            <>
              <h2 className="font-display text-2xl font-bold text-zinc-900">Choose a new password</h2>
              <p className="text-sm text-zinc-600 mt-2">
                For your security, pick a password you haven't used elsewhere.
              </p>

              <form onSubmit={onSubmit} className="mt-7 space-y-4">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                    New password
                  </label>
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={!token || submitting}
                    data-testid="reset-password-input"
                    className="mt-1.5 w-full bg-white border border-zinc-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF9800] focus:border-[#FF9800]"
                    placeholder="••••••••"
                    required
                    minLength={6}
                  />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                    Confirm password
                  </label>
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    disabled={!token || submitting}
                    data-testid="reset-confirm-input"
                    className="mt-1.5 w-full bg-white border border-zinc-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF9800] focus:border-[#FF9800]"
                    placeholder="••••••••"
                    required
                    minLength={6}
                  />
                </div>

                {error && (
                  <div className="flex items-start gap-2 bg-red-50 border border-red-200 p-3 text-sm text-red-700">
                    <WarningCircle size={16} weight="fill" className="flex-shrink-0 mt-0.5" />
                    <span>{error}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={!token || submitting}
                  data-testid="reset-submit-button"
                  className="w-full bg-[#FF9800] hover:bg-[#E68900] text-[#1A2332] font-bold py-3 px-4 flex items-center justify-center gap-2 transition-colors active:scale-[0.99] disabled:opacity-60"
                >
                  {submitting ? "Updating…" : (
                    <>Update password <ArrowRight size={16} weight="bold" /></>
                  )}
                </button>

                <div className="text-center pt-2">
                  <Link
                    to="/login"
                    className="text-xs font-bold uppercase tracking-widest text-zinc-500 hover:text-[#FF9800]"
                  >
                    Back to sign in
                  </Link>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
